import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import FullScreenModal from "@/components/FullScreenModal";
import { useMe } from "@vaulzo/core/hooks/useMe";
import { updateProfile, changePassword } from "@vaulzo/core/api/user";
import { apiErrorDetail } from "@vaulzo/core/http";
import { useTheme } from "@/theme";

const underline =
  "w-full border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-base text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

const label = "mb-[10px] block text-[13px] font-semibold text-vaulzo-muted";

const primaryBtn =
  "flex items-center justify-center rounded-[11px] bg-vaulzo-accent px-[26px] py-[12px] text-[14px] font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60";

/**
 * Account profile: display names (drive the header avatar/menu) and a
 * change-password form, each saving independently.
 */
export default function ProfileModal({ open, onClose }) {
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const [theme, setTheme] = useTheme();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName(me?.first_name || "");
      setLastName(me?.last_name || "");
      setCurrentPassword("");
      setNewPassword("");
    }
  }, [open, me]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(apiErrorDetail(err) || "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Password updated!");
    } catch (err) {
      toast.error(apiErrorDetail(err) || "Could not update password");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className="w-full max-w-[520px]">
        <div className="mb-[14px] text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">
          Account
        </div>
        <h2 className="m-0 mb-2 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">Profile</h2>
        <div className="mb-10 text-sm text-vaulzo-faint">{me?.email}</div>

        {/* Display names */}
        <form onSubmit={saveProfile} className="flex flex-col gap-[26px]">
          <div className="flex flex-col gap-[26px] sm:flex-row sm:gap-6">
            <label className="block flex-1">
              <span className={label}>First name</span>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. Sameer"
                maxLength={60}
                className={underline}
              />
            </label>
            <label className="block flex-1">
              <span className={label}>Last name</span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Choubey"
                maxLength={60}
                className={underline}
              />
            </label>
          </div>
          <button type="submit" disabled={savingProfile} className={`self-start ${primaryBtn}`}>
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Save profile"}
          </button>
        </form>

        {/* Appearance: per device, remembered in this browser */}
        <div className="mt-12 border-t border-vaulzo-hairline pt-8">
          <h3 className="m-0 mb-2 text-[18px] font-bold tracking-[-0.02em] text-vaulzo-ink">Appearance</h3>
          <p className="m-0 mb-5 text-sm text-vaulzo-faint">Remembered on this device.</p>
          <div className="inline-flex gap-[2px] rounded-[11px] bg-vaulzo-row p-1" role="radiogroup" aria-label="Appearance">
            {[
              ["light", "Light"],
              ["dark", "Dark"],
            ].map(([value, text]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                onClick={() => setTheme(value)}
                className={`rounded-lg px-[18px] py-[7px] text-[13px] transition-all ${
                  theme === value
                    ? "bg-vaulzo-surface font-semibold text-vaulzo-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                    : "bg-transparent font-medium text-vaulzo-faint hover:text-vaulzo-ink"
                }`}
              >
                {text}
              </button>
            ))}
          </div>
        </div>

        {/* Change password */}
        <div className="mt-12 border-t border-vaulzo-hairline pt-8">
          <h3 className="m-0 mb-6 text-[18px] font-bold tracking-[-0.02em] text-vaulzo-ink">
            Change password
          </h3>
          <form onSubmit={savePassword} className="flex flex-col gap-[26px]">
            <label className="block">
              <span className={label}>Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className={underline}
              />
            </label>
            <label className="block">
              <span className={label}>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className={underline}
              />
            </label>
            <button type="submit" disabled={savingPassword} className={`self-start ${primaryBtn}`}>
              {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Update password"}
            </button>
          </form>
        </div>
      </div>
    </FullScreenModal>
  );
}
